import styled, { css } from "styled-components";

export type SchoolMatchWeekHighlightVariant = "player" | "team" | "match" | "venue";

const featuredSurfaceStyle = css`
  border-color: rgba(240, 191, 86, 0.18);
  background: linear-gradient(
    180deg,
    rgba(240, 191, 86, 0.1) 0%,
    rgba(255, 255, 255, 0.03) 100%
  );
`;

const neutralSurfaceStyle = css`
  border-color: rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.035);
`;

const variantSurfaceStyles: Record<SchoolMatchWeekHighlightVariant, ReturnType<typeof css>> = {
  player: featuredSurfaceStyle,
  team: neutralSurfaceStyle,
  match: neutralSurfaceStyle,
  venue: neutralSurfaceStyle,
};

const variantAccentColors: Record<SchoolMatchWeekHighlightVariant, string> = {
  player: "var(--brand-highlight)",
  team: "rgba(255, 255, 255, 0.1)",
  match: "rgba(255, 255, 255, 0.1)",
  venue: "rgba(255, 255, 255, 0.1)",
};

const variantEyebrowColors: Record<SchoolMatchWeekHighlightVariant, string> = {
  player: "var(--brand-highlight)",
  team: "var(--brand-muted)",
  match: "var(--brand-muted)",
  venue: "var(--brand-muted)",
};

const featuredIconStyle = css`
  border-color: rgba(240, 191, 86, 0.2);
  background: rgba(240, 191, 86, 0.12);
  color: var(--brand-highlight);
`;

const neutralIconStyle = css`
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: var(--brand-muted);
`;

const variantIconStyles: Record<SchoolMatchWeekHighlightVariant, ReturnType<typeof css>> = {
  player: featuredIconStyle,
  team: neutralIconStyle,
  match: neutralIconStyle,
  venue: neutralIconStyle,
};

export const SchoolMatchWeekHighlightsGrid = styled.div.attrs({
  className: "grid gap-3 md:grid-cols-2 xl:grid-cols-4",
})``;

export const SchoolMatchWeekHighlightsFooter = styled.div.attrs({
  className: "mt-5 space-y-5",
})``;

export const SchoolMatchWeekHighlightCard = styled.div<{
  $interactive?: boolean;
  $variant: SchoolMatchWeekHighlightVariant;
}>`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  border-width: 1px;
  border-style: solid;
  padding: 1rem;
  text-align: left;
  transition:
    border-color 160ms ease,
    transform 160ms ease,
    box-shadow 160ms ease;

  ${({ $variant }) => variantSurfaceStyles[$variant]}

  ${({ $interactive }) =>
    $interactive
      ? css`
          cursor: pointer;

          &:hover {
            border-color: rgba(240, 191, 86, 0.34);
            transform: translateY(-1px);
            box-shadow: 0 14px 28px rgba(0, 0, 0, 0.18);
          }
        `
      : ""}
`;

export const SchoolMatchWeekHighlightAccent = styled.div<{
  $variant: SchoolMatchWeekHighlightVariant;
}>`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  background: ${({ $variant }) => variantAccentColors[$variant]};
`;

export const SchoolMatchWeekHighlightHeader = styled.div.attrs({
  className: "mb-3 flex items-start justify-between gap-3",
})``;

export const SchoolMatchWeekHighlightEyebrow = styled.p<{
  $variant: SchoolMatchWeekHighlightVariant;
}>`
  font-family: var(--font-body);
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  line-height: 1.4;
  text-transform: uppercase;
  color: ${({ $variant }) => variantEyebrowColors[$variant]};
`;

export const SchoolMatchWeekHighlightIconWrap = styled.span<{
  $variant: SchoolMatchWeekHighlightVariant;
}>`
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 0.9rem;
  border-width: 1px;
  border-style: solid;

  ${({ $variant }) => variantIconStyles[$variant]}
`;

export const SchoolMatchWeekHighlightValue = styled.p.attrs({
  className:
    "break-words text-base font-semibold leading-snug text-[var(--brand-ink)] sm:text-[1.05rem]",
})``;

export const SchoolMatchWeekHighlightPending = styled.span.attrs({
  className: "font-medium text-[var(--brand-muted)]",
})``;

export const SchoolMatchWeekHighlightMeta = styled.p.attrs({
  className: "mt-2 text-xs leading-5 text-[var(--brand-muted)]",
})``;

export const SchoolMatchWeekHighlightsSkeletonGrid = styled.div.attrs({
  className: "grid gap-3 md:grid-cols-2 xl:grid-cols-4",
})``;

export const SchoolMatchWeekHighlightSkeletonCard = styled.div.attrs({
  className:
    "relative overflow-hidden rounded-[22px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] p-4",
})``;

export const SchoolMatchWeekHighlightSkeletonAccent = styled.div.attrs({
  className: "brand-skeleton absolute inset-x-0 top-0 h-[3px] rounded-none",
})``;
